// The venue's rate card (spec/13 §18, spec/06 §4; F-PRC-10, F-PRC-11, F-PRC-12): pay as you go priced per station
// class, with time-of-day bands, the billing step and a minimum charge — read as one table, edited in three steps.
//
//   GET  …/venues/{v}/rate-card   the version in force, what each class pays now, scheduled versions   pricing:read
//   POST …/venues/{v}/rate-card   publish a new version, now or from a later moment                  pricing:manage
//
// The table is the card as the server prices it: each class of the venue, what it pays **now** (the band open in the
// venue's timezone, and when that changes — resolved by the server, never here), its hourly rate, its billing step,
// its minimum and its bands, and a worked example of one session. A class the card leaves out keeps the venue's flat
// rate, and says so. Editing is a stepper — **Rates** (which classes the card prices, and at what), **Time bands**
// (off-peak or late-night prices per class) and **Review** (a name, when it starts, and what it will charge) — typed in
// rupees and sent as paise. Publishing makes a new version; a running session keeps the rate it started at. Daily caps
// are not charged by the sessions spine yet, so the editor does not offer one (spec/06 §4 implementation status).
//
// A class with staff-run stations (consoles, sims, tables — ADR-0034) is priced by **controllers** too: a first- and an
// extra-controller hourly rate, optional fixed prices for the 30-minute, 1-, 2- and 5-hour blocks, and the unit an Open
// session's time played rounds up to. A booked console session is paid once at the end from these.

import type { Money, PublishRateCard, RateCardRate, RateCardView, StationClass } from '@clavian/api-client';
import { breakdownCharge } from '@clavian/domain/funding-breakdown';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Chip, Dialog, SegmentedControl, Skeleton, Stepper, Switch, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { LoadFailure } from '../../shell/LoadFailure';
import { useStationClasses, useStations } from '../../stations/queries';
import { Notice } from '../../ui';
import { majorToMinor, minorToMajor } from '../money';
import { useServerErrors } from '../server-errors';
import { usePublishRateCard, useRateCard } from './queries';

const UNITS = ['60', '300', '900', '1800'] as const;
/** The units an Open console session rounds its time played up to (ADR-0034). */
const OPEN_UNITS = ['60', '300', '900', '1800', '3600'] as const;
/** The blocks a class may give a fixed first-controller price, in minutes: the start form's chips. */
const BLOCKS = ['30', '60', '120', '300'] as const;
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun', 'holiday'] as const;
/** The worked example: a 90-minute session, starting now. */
const EXAMPLE_SECONDS = 5400;

interface DraftBand {
  readonly days: readonly string[];
  readonly start: string;
  readonly end: string;
  readonly hourly: string;
}

/** A staff-run class's controller pricing being edited (ADR-0034), typed in rupees. */
interface DraftControllers {
  readonly on: boolean;
  readonly first: string;
  readonly extra: string;
  readonly blocks: Readonly<Record<(typeof BLOCKS)[number], string>>;
  readonly openUnit: (typeof OPEN_UNITS)[number];
}

interface DraftLine {
  readonly included: boolean;
  readonly hourly: string;
  readonly unit: (typeof UNITS)[number];
  readonly minimumMinutes: string;
  readonly bands: readonly DraftBand[];
  readonly controllers: DraftControllers;
}

/** "30 min", "1 h", "5 h" — a block's length. */
function blockLabel(minutes: number, t: (key: string, options?: Record<string, unknown>) => string): string {
  return minutes < 60 ? t('consoleSession.minutes', { n: minutes }) : t('consoleSession.hours', { n: minutes / 60 });
}

export function RateCardSection({ organizationId, venueId, canManage }: { readonly organizationId: string; readonly venueId: string; readonly canManage: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const card = useRateCard(organizationId, venueId);
  const classes = useStationClasses(organizationId, venueId);
  const stations = useStations(organizationId, venueId);
  // The classes with a staff-run station (ADR-0034): the editor prices them by controllers too.
  const staffRunClasses = new Set((stations.data?.pages ?? []).flatMap((page) => page.data).filter((station) => station.managed === false).map((station) => station.classId));
  const [editing, setEditing] = useState(false);

  if (card.data === undefined || classes.data === undefined) {
    if (card.isError && problemCode(card.error) === PROBLEM.forbidden) return null;
    return card.isError || classes.isError ? (
      <LoadFailure error={card.error ?? classes.error} message={t('pricing.rateCard.unavailable')} onRetry={() => void card.refetch()} />
    ) : (
      <Skeleton height="12rem" />
    );
  }

  const view = card.data;
  const current = view.current;
  const flat = format.money(view.fallback.hourlyAmount);
  const lineOf = (classId: string): RateCardRate | undefined => current?.rates.find((rate) => rate.stationClassId === classId);
  const nowOf = (classId: string) => view.now.find((entry) => entry.stationClassId === classId);
  const example = classes.data.map((cls) => ({ cls, line: lineOf(cls.id), now: nowOf(cls.id) })).find((entry) => entry.line !== undefined && entry.now !== undefined);

  return (
    <section className="console-pricing-card console-rail" data-rail={current === null ? undefined : 'ok'} aria-labelledby="console-rate-card-title">
      <div className="console-pricing-card__head">
        <div className="console-pricing-card__lede">
          <h2 id="console-rate-card-title" className="console-pricing-card__title">
            {t('pricing.rateCard.title')}
          </h2>
          <p className="console-subtle">
            {current === null
              ? t('pricing.rateCard.none', { rate: flat })
              : t('pricing.rateCard.inForce', { version: current.version, name: current.name, since: format.dateTime(current.effectiveFrom) })}
          </p>
        </div>
        {canManage ? (
          <Button variant="primary" onClick={() => setEditing(true)}>
            {current === null ? t('pricing.rateCard.create') : t('pricing.rateCard.edit')}
          </Button>
        ) : null}
      </div>

      {view.payAsYouGoEnabled ? null : <Notice role="note">{t('pricing.rateCard.paygOff')}</Notice>}

      <div className="clav-table__scroll">
        <table className="clav-table console-ratecard">
          <caption className="clav-visually-hidden">{t('pricing.rateCard.caption')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('pricing.rateCard.class')}</th>
              <th scope="col">{t('pricing.rateCard.now')}</th>
              <th scope="col">{t('pricing.rateCard.rate')}</th>
              <th scope="col">{t('pricing.rateCard.step')}</th>
              <th scope="col">{t('pricing.rateCard.minimum')}</th>
              <th scope="col">{t('pricing.rateCard.bands')}</th>
            </tr>
          </thead>
          <tbody>
            {classes.data.map((cls) => {
              const line = lineOf(cls.id);
              const now = nowOf(cls.id);
              return (
                <tr key={cls.id}>
                  <th scope="row">{cls.name}</th>
                  {line === undefined ? (
                    // With no card at all the description above already gives the flat rate: each row just says so.
                    <td colSpan={5} className="console-subtle">
                      {current === null ? t('pricing.rateCard.flatShort') : t('pricing.rateCard.flat', { rate: flat })}
                    </td>
                  ) : (
                    <>
                      <td>
                        <span className="console-money">{now === undefined ? '—' : t('pricing.rateCard.perHour', { rate: format.money(now.hourlyAmount) })}</span>
                        {now?.nextChange ? (
                          <span className="console-ratecard__next">
                            {t('pricing.rateCard.nextChange', { rate: format.money(now.nextChange.hourlyAmount), time: format.time(now.nextChange.at) })}
                          </span>
                        ) : null}
                      </td>
                      <td className="console-money">
                        {t('pricing.rateCard.perHour', { rate: format.money(line.controllers?.firstControllerHourly ?? line.hourlyAmount) })}
                        {line.controllers === undefined ? null : (
                          <span className="console-ratecard__next">
                            {t('pricing.rateCard.controllers.summary', { extra: format.money(line.controllers.extraControllerHourly) })}
                            {line.controllers.blockPrices.length === 0
                              ? null
                              : ` · ${line.controllers.blockPrices.map((block) => `${blockLabel(block.minutes, t)} ${format.money(block.amount)}`).join(', ')}`}
                          </span>
                        )}
                      </td>
                      <td>{t('pricing.rateCard.stepMinutes', { count: line.billingUnitSeconds / 60 })}</td>
                      <td>{line.minimumChargeSeconds === 0 ? t('pricing.rateCard.noMinimum') : t('pricing.rateCard.minimumMinutes', { count: line.minimumChargeSeconds / 60 })}</td>
                      <td>
                        {line.bands.length === 0
                          ? t('pricing.rateCard.noBands')
                          : line.bands.map((band) => (
                              <span key={`${band.days.join()}-${band.start}`} className="console-ratecard__band">
                                {t('pricing.rateCard.band', { days: band.days.map((day) => t(`pricing.rateCard.day.${day}`)).join(', '), start: band.start, end: band.end, rate: format.money(band.hourlyAmount) })}
                              </span>
                            ))}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {example?.line !== undefined && example.now !== undefined ? (
        <p className="console-subtle">
          {t('pricing.rateCard.example', {
            class: example.cls.name,
            amount: format.money(exampleCharge(example.now.hourlyAmount, example.line)),
            rate: format.money(example.now.hourlyAmount),
          })}
        </p>
      ) : null}

      {view.scheduled.map((version) => (
        <p key={version.id} className="console-subtle">
          <Badge tone="info">{t('pricing.rateCard.scheduled')}</Badge> {t('pricing.rateCard.scheduledFrom', { version: version.version, name: version.name, from: format.dateTime(version.effectiveFrom) })}
        </p>
      ))}

      <Dialog open={editing} onOpenChange={setEditing} size="lg" dirty="auto" title={t('pricing.rateCard.editTitle')} description={t('pricing.rateCard.editHint')}>
        {editing ? <RateCardFlow organizationId={organizationId} venueId={venueId} view={view} classes={classes.data} staffRunClasses={staffRunClasses} onDone={() => setEditing(false)} /> : null}
      </Dialog>
    </section>
  );
}

/** What the worked example costs: 90 minutes at the rate now, never less than the class's minimum (spec/06 §4). */
function exampleCharge(hourlyAmount: Money, line: RateCardRate): Money {
  const rate = { hourlyAmount, billingUnitSeconds: line.billingUnitSeconds };
  const charge = breakdownCharge(EXAMPLE_SECONDS, rate);
  const minimum = line.minimumChargeSeconds > 0 ? breakdownCharge(line.minimumChargeSeconds, rate) : charge;
  return charge.amount >= minimum.amount ? charge : minimum;
}

// -------------------------------------------------------------------------------------------------
// The editor: Rates → Time bands → Review
// -------------------------------------------------------------------------------------------------

function draftFrom(view: RateCardView, classes: readonly StationClass[], staffRunClasses: ReadonlySet<string>): Record<string, DraftLine> {
  const currency = view.currency;
  const lines: Record<string, DraftLine> = {};
  for (const cls of classes) {
    const line = view.current?.rates.find((rate) => rate.stationClassId === cls.id);
    const hourly = minorToMajor((line?.hourlyAmount ?? view.fallback.hourlyAmount).amount, currency);
    const pricing = line?.controllers;
    const blockOf = (minutes: number): string => {
      const block = pricing?.blockPrices.find((one) => one.minutes === minutes);
      return block === undefined ? '' : minorToMajor(block.amount.amount, currency);
    };
    const controllers: DraftControllers = {
      on: pricing !== undefined || staffRunClasses.has(cls.id),
      first: pricing === undefined ? hourly : minorToMajor(pricing.firstControllerHourly.amount, currency),
      extra: pricing === undefined ? hourly : minorToMajor(pricing.extraControllerHourly.amount, currency),
      blocks: { '30': blockOf(30), '60': blockOf(60), '120': blockOf(120), '300': blockOf(300) },
      openUnit: pricing === undefined ? '900' : ((OPEN_UNITS as readonly string[]).includes(String(pricing.billingUnitSeconds)) ? (String(pricing.billingUnitSeconds) as (typeof OPEN_UNITS)[number]) : '900'),
    };
    lines[cls.id] =
      line === undefined
        ? { included: false, hourly, unit: unitOf(view.fallback.billingUnitSeconds), minimumMinutes: '0', bands: [], controllers }
        : {
            included: true,
            hourly,
            unit: unitOf(line.billingUnitSeconds),
            minimumMinutes: String(line.minimumChargeSeconds / 60),
            bands: line.bands.map((band) => ({ days: band.days, start: band.start, end: band.end, hourly: minorToMajor(band.hourlyAmount.amount, currency) })),
            controllers,
          };
  }
  return lines;
}

function unitOf(seconds: number): (typeof UNITS)[number] {
  return (UNITS as readonly string[]).includes(String(seconds)) ? (String(seconds) as (typeof UNITS)[number]) : '60';
}

/** Whether the card in force prices a class by controllers already. */
function lineHasControllers(view: RateCardView, classId: string): boolean {
  return view.current?.rates.find((rate) => rate.stationClassId === classId)?.controllers !== undefined;
}

/**
 * A staff-run class's controller pricing (ADR-0034): the first and each extra controller per hour, fixed prices for
 * the start form's blocks (blank keeps the rate for the block's length), and the unit an Open session rounds up to.
 */
function ControllerPricing({
  name,
  currency,
  draft,
  onChange,
  errorOf,
  onBlur,
  clear,
}: {
  readonly name: string;
  readonly currency: string;
  readonly draft: DraftControllers;
  readonly onChange: (patch: Partial<DraftControllers>) => void;
  /** This class's controller-field errors, keyed `first`, `extra` or a block's minutes. */
  readonly errorOf: (field: 'first' | 'extra' | (typeof BLOCKS)[number]) => string | undefined;
  readonly onBlur: (field: 'first' | 'extra' | (typeof BLOCKS)[number]) => void;
  readonly clear: (field: 'first' | 'extra' | (typeof BLOCKS)[number]) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <fieldset className="console-choice-group">
      <legend className="clav-field__label">{t('pricing.rateCard.controllers.title')}</legend>
      <p className="clav-field__description">{t('pricing.rateCard.controllers.hint')}</p>
      <Switch checked={draft.on} onCheckedChange={(on) => onChange({ on })} label={t('pricing.rateCard.controllers.on', { class: name })} />
      {draft.on ? (
        <>
          <div className="console-form__row">
            <TextField
              label={t('pricing.rateCard.controllers.first', { currency })}
              inputMode="decimal"
              value={draft.first}
              onChange={(e) => {
                onChange({ first: e.target.value });
                clear('first');
              }}
              onBlur={() => onBlur('first')}
              error={errorOf('first')}
            />
            <TextField
              label={t('pricing.rateCard.controllers.extra', { currency })}
              inputMode="decimal"
              value={draft.extra}
              onChange={(e) => {
                onChange({ extra: e.target.value });
                clear('extra');
              }}
              onBlur={() => onBlur('extra')}
              error={errorOf('extra')}
            />
          </div>
          <span className="clav-field__label">{t('pricing.rateCard.controllers.blocks')}</span>
          <div className="console-form__row">
            {BLOCKS.map((minutes) => (
              <TextField
                key={minutes}
                label={t('pricing.rateCard.controllers.block', { block: blockLabel(Number(minutes), t), currency })}
                showOptional
                inputMode="decimal"
                value={draft.blocks[minutes]}
                onChange={(e) => {
                  onChange({ blocks: { ...draft.blocks, [minutes]: e.target.value } });
                  clear(minutes);
                }}
                onBlur={() => onBlur(minutes)}
                error={errorOf(minutes)}
              />
            ))}
          </div>
          <p className="clav-field__description">{t('pricing.rateCard.controllers.blocksHint')}</p>
          <SegmentedControl<(typeof OPEN_UNITS)[number]>
            label={t('pricing.rateCard.controllers.openUnit')}
            value={draft.openUnit}
            onValueChange={(openUnit) => onChange({ openUnit })}
            options={OPEN_UNITS.map((unit) => ({ value: unit, label: t('pricing.rateCard.stepMinutes', { count: Number(unit) / 60 }) }))}
          />
        </>
      ) : null}
    </fieldset>
  );
}

function RateCardFlow({
  organizationId,
  venueId,
  view,
  classes,
  staffRunClasses,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly view: RateCardView;
  readonly classes: readonly StationClass[];
  /** The classes with a staff-run station (ADR-0034): offered controller pricing. */
  readonly staffRunClasses: ReadonlySet<string>;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const { show } = useToast();
  const publish = usePublishRateCard(organizationId, venueId);
  const serverErrors = useServerErrors();
  const currency = view.currency;
  const [step, setStep] = useState(0);
  const [lines, setLines] = useState<Record<string, DraftLine>>(() => draftFrom(view, classes, staffRunClasses));
  const [name, setName] = useState(view.current?.name ?? t('pricing.rateCard.defaultName'));
  const [later, setLater] = useState(false);
  const [from, setFrom] = useState('');
  // A problem on a field sits under that field (shown and re-checked as the step is left and on blur, like the
  // invite and new-role forms); `formError` is for a failure the whole form shares — the server's refusal.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const setLine = (classId: string, patch: Partial<DraftLine>): void => setLines((all) => ({ ...all, [classId]: { ...all[classId]!, ...patch } }));
  const setBand = (classId: string, index: number, patch: Partial<DraftBand>): void =>
    setLine(classId, { bands: lines[classId]!.bands.map((band, at) => (at === index ? { ...band, ...patch } : band)) });
  const included = classes.filter((cls) => lines[cls.id]?.included === true);

  // The field-error keys, so a field, its blur check and the step check all name the same error.
  const FE = {
    rate: (c: string) => `rate:${c}`,
    min: (c: string) => `min:${c}`,
    cfirst: (c: string) => `cfirst:${c}`,
    cextra: (c: string) => `cextra:${c}`,
    cblock: (c: string, m: string) => `cblock:${c}:${m}`,
    bdays: (c: string, i: number) => `bdays:${c}:${i}`,
    bend: (c: string, i: number) => `bend:${c}:${i}`,
    brate: (c: string, i: number) => `brate:${c}:${i}`,
  } as const;
  const setField = (key: string, message: string | undefined): void =>
    setFieldErrors((prev) => {
      if (message === undefined) {
        if (prev[key] === undefined) return prev;
        const rest = { ...prev };
        delete rest[key];
        return rest;
      }
      return prev[key] === message ? prev : { ...prev, [key]: message };
    });
  const clearField = (key: string): void => setField(key, undefined);

  // Per-field checks (pure): each returns the message to show, or undefined when the field is right. A money
  // field is paise once typed in rupees; a blank minimum means none.
  const rateMsg = (rupees: string): string | undefined => {
    const paise = majorToMinor(rupees, currency);
    return paise === null || paise < 0 ? t('pricing.rateCard.fieldError.rate') : undefined;
  };
  const minMsg = (minutes: string): string | undefined => {
    const n = Number(minutes || '0');
    return !Number.isInteger(n) || n < 0 || n > 60 ? t('pricing.rateCard.fieldError.minimum') : undefined;
  };
  const amountMsg = (rupees: string): string | undefined => {
    const paise = majorToMinor(rupees, currency);
    return paise === null || paise < 0 ? t('pricing.rateCard.fieldError.controller') : undefined;
  };
  const blockMsg = (rupees: string): string | undefined => (rupees.trim() === '' ? undefined : amountMsg(rupees));
  const daysMsg = (days: readonly string[]): string | undefined => (days.length === 0 ? t('pricing.rateCard.fieldError.bandDays') : undefined);
  const bandTimeMsg = (start: string, end: string): string | undefined =>
    !/^\d\d:\d\d$/.test(start) || !/^\d\d:\d\d$/.test(end) || start === end ? t('pricing.rateCard.fieldError.bandTime') : undefined;
  const bandRateMsg = (rupees: string): string | undefined => {
    const paise = majorToMinor(rupees, currency);
    return paise === null || paise < 0 ? t('pricing.rateCard.fieldError.bandRate') : undefined;
  };
  /** Whether this class is priced by controllers on the current card or by a staff-run station. */
  const controllersApply = (classId: string): boolean => staffRunClasses.has(classId) || lineHasControllers(view, classId);

  /** Check the Rates step; set every field's error at once and say whether it is clean. */
  function validateRates(): boolean {
    const next: Record<string, string> = {};
    for (const cls of included) {
      const line = lines[cls.id]!;
      const rate = rateMsg(line.hourly);
      if (rate !== undefined) next[FE.rate(cls.id)] = rate;
      const minimum = minMsg(line.minimumMinutes);
      if (minimum !== undefined) next[FE.min(cls.id)] = minimum;
      if (line.controllers.on && controllersApply(cls.id)) {
        const first = amountMsg(line.controllers.first);
        if (first !== undefined) next[FE.cfirst(cls.id)] = first;
        const extra = amountMsg(line.controllers.extra);
        if (extra !== undefined) next[FE.cextra(cls.id)] = extra;
        for (const minutes of BLOCKS) {
          const block = blockMsg(line.controllers.blocks[minutes]);
          if (block !== undefined) next[FE.cblock(cls.id, minutes)] = block;
        }
      }
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  /** Check the Time bands step the same way. */
  function validateBands(): boolean {
    const next: Record<string, string> = {};
    for (const cls of included) {
      lines[cls.id]!.bands.forEach((band, index) => {
        const days = daysMsg(band.days);
        if (days !== undefined) next[FE.bdays(cls.id, index)] = days;
        const time = bandTimeMsg(band.start, band.end);
        if (time !== undefined) next[FE.bend(cls.id, index)] = time;
        const rate = bandRateMsg(band.hourly);
        if (rate !== undefined) next[FE.brate(cls.id, index)] = rate;
      });
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  /** The draft as the contract carries it, or the first thing wrong with it. */
  function build(): { readonly body?: PublishRateCard; readonly problem?: string } {
    const rates: RateCardRate[] = [];
    for (const cls of included) {
      const line = lines[cls.id]!;
      const hourly = majorToMinor(line.hourly, currency);
      if (hourly === null || hourly < 0) return { problem: t('pricing.rateCard.problem.rate', { class: cls.name }) };
      const minimum = Number(line.minimumMinutes || '0');
      if (!Number.isInteger(minimum) || minimum < 0 || minimum > 60) return { problem: t('pricing.rateCard.problem.minimum', { class: cls.name }) };
      const bands = [];
      for (const band of line.bands) {
        const amount = majorToMinor(band.hourly, currency);
        if (amount === null || amount < 0 || band.days.length === 0 || !/^\d\d:\d\d$/.test(band.start) || !/^\d\d:\d\d$/.test(band.end) || band.start === band.end) {
          return { problem: t('pricing.rateCard.problem.band', { class: cls.name }) };
        }
        bands.push({ days: band.days, start: band.start, end: band.end, hourlyAmount: { amount, currency } });
      }
      // A staff-run class's controller pricing (ADR-0034): the rates, the blocks given a price, and the Open unit.
      let controllers: RateCardRate['controllers'];
      if (line.controllers.on && (staffRunClasses.has(cls.id) || lineHasControllers(view, cls.id))) {
        const first = majorToMinor(line.controllers.first, currency);
        const extra = majorToMinor(line.controllers.extra, currency);
        if (first === null || first < 0 || extra === null || extra < 0) return { problem: t('pricing.rateCard.controllers.problem', { class: cls.name }) };
        const blockPrices = [];
        for (const minutes of BLOCKS) {
          const typed = line.controllers.blocks[minutes].trim();
          if (typed === '') continue;
          const amount = majorToMinor(typed, currency);
          if (amount === null || amount < 0) return { problem: t('pricing.rateCard.controllers.problem', { class: cls.name }) };
          blockPrices.push({ minutes: Number(minutes), amount: { amount, currency } });
        }
        controllers = { firstControllerHourly: { amount: first, currency }, extraControllerHourly: { amount: extra, currency }, blockPrices, billingUnitSeconds: Number(line.controllers.openUnit) } as RateCardRate['controllers'];
      }
      rates.push({
        stationClassId: cls.id,
        hourlyAmount: { amount: hourly, currency },
        billingUnitSeconds: Number(line.unit),
        minimumChargeSeconds: minimum * 60,
        dailyCapAmount: null,
        bands,
        ...(controllers === undefined ? {} : { controllers }),
      } as RateCardRate);
    }
    if (name.trim() === '') return { problem: t('pricing.rateCard.problem.name') };
    const effectiveFrom = later && from !== '' ? new Date(from).toISOString() : undefined;
    return { body: { name: name.trim(), rates, ...(effectiveFrom === undefined ? {} : { effectiveFrom }) } as PublishRateCard };
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    // Each step is checked before it is left: a problem keeps you on the step, under the field it belongs to.
    if (step === 0) {
      if (validateRates()) setStep(1);
      return;
    }
    if (step === 1) {
      if (validateBands()) setStep(2);
      return;
    }
    if (name.trim() === '') {
      setFieldErrors({ name: t('pricing.rateCard.fieldError.name') });
      return;
    }
    const built = build();
    if (built.body === undefined) {
      // A safety net: the steps are checked before this one, so this is reached only if a value slipped through.
      setFormError(built.problem ?? t('pricing.rateCard.problem.publish'));
      return;
    }
    setFieldErrors({});
    setFormError(null);
    publish.mutate(built.body, {
      onSuccess: () => {
        show({ tone: 'ok', title: t('pricing.rateCard.published') });
        onDone();
      },
      onError: (failure) => {
        const refused = serverErrors(failure, t('pricing.rateCard.problem.publish'), { name: 'name' });
        const { form, ...fields } = refused;
        setFieldErrors(fields);
        setFormError(form ?? null);
        if (fields['name'] !== undefined) setStep(2);
      },
    });
  }

  return (
    <div className="console-stepper-flow">
      <Stepper
        current={step}
        onStepSelect={(index) => (index < step ? setStep(index) : undefined)}
        steps={[
          { id: 'rates', label: t('pricing.rateCard.stepRates') },
          { id: 'bands', label: t('pricing.rateCard.stepBands') },
          { id: 'review', label: t('pricing.rateCard.stepReview') },
        ]}
      />
      <form className="console-form" onSubmit={submit} noValidate>
        {formError === null ? null : (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        )}

        {step === 0
          ? classes.map((cls) => {
              const line = lines[cls.id]!;
              return (
                <fieldset key={cls.id} className="console-choice-group console-ratecard__line">
                  <legend className="clav-field__label">{cls.name}</legend>
                  <Switch
                    checked={line.included}
                    onCheckedChange={(value) => setLine(cls.id, { included: value })}
                    label={t('pricing.rateCard.priced', { rate: format.money(view.fallback.hourlyAmount) })}
                  />
                  {line.included ? (
                    <>
                      <TextField
                        label={t('pricing.rateCard.hourly', { class: cls.name })}
                        inputMode="decimal"
                        value={line.hourly}
                        onChange={(e) => {
                          setLine(cls.id, { hourly: e.target.value });
                          clearField(FE.rate(cls.id));
                        }}
                        onBlur={() => setField(FE.rate(cls.id), rateMsg(line.hourly))}
                        error={fieldErrors[FE.rate(cls.id)]}
                      />
                      <SegmentedControl<(typeof UNITS)[number]>
                        label={t('pricing.rateCard.step')}
                        value={line.unit}
                        onValueChange={(unit) => setLine(cls.id, { unit })}
                        options={UNITS.map((unit) => ({ value: unit, label: t('pricing.rateCard.stepMinutes', { count: Number(unit) / 60 }) }))}
                      />
                      <TextField
                        label={t('pricing.rateCard.minimumLabel', { class: cls.name })}
                        description={t('pricing.rateCard.minimumHint')}
                        inputMode="numeric"
                        value={line.minimumMinutes}
                        onChange={(e) => {
                          setLine(cls.id, { minimumMinutes: e.target.value });
                          clearField(FE.min(cls.id));
                        }}
                        onBlur={() => setField(FE.min(cls.id), minMsg(line.minimumMinutes))}
                        error={fieldErrors[FE.min(cls.id)]}
                      />
                      {controllersApply(cls.id) ? (
                        <ControllerPricing
                          name={cls.name}
                          currency={currency}
                          draft={line.controllers}
                          onChange={(controllers) => setLine(cls.id, { controllers: { ...line.controllers, ...controllers } })}
                          errorOf={(field) => fieldErrors[field === 'first' ? FE.cfirst(cls.id) : field === 'extra' ? FE.cextra(cls.id) : FE.cblock(cls.id, field)]}
                          onBlur={(field) =>
                            field === 'first'
                              ? setField(FE.cfirst(cls.id), amountMsg(line.controllers.first))
                              : field === 'extra'
                                ? setField(FE.cextra(cls.id), amountMsg(line.controllers.extra))
                                : setField(FE.cblock(cls.id, field), blockMsg(line.controllers.blocks[field]))
                          }
                          clear={(field) => clearField(field === 'first' ? FE.cfirst(cls.id) : field === 'extra' ? FE.cextra(cls.id) : FE.cblock(cls.id, field))}
                        />
                      ) : null}
                    </>
                  ) : null}
                </fieldset>
              );
            })
          : null}

        {step === 1 ? (
          included.length === 0 ? (
            <p className="console-subtle">{t('pricing.rateCard.noClasses')}</p>
          ) : (
            included.map((cls) => {
              const line = lines[cls.id]!;
              return (
                <fieldset key={cls.id} className="console-choice-group console-ratecard__line">
                  <legend className="clav-field__label">{cls.name}</legend>
                  <p className="clav-field__description">{t('pricing.rateCard.bandsHint')}</p>
                  {line.bands.map((band, index) => (
                    <div key={index} className="console-ratecard__band-edit">
                      <div className="console-chips">
                        {DAYS.map((day) => (
                          <Chip
                            key={day}
                            selected={band.days.includes(day)}
                            onSelectedChange={(on) => {
                              setBand(cls.id, index, { days: on ? [...band.days, day] : band.days.filter((d) => d !== day) });
                              clearField(FE.bdays(cls.id, index));
                            }}
                          >
                            {t(`pricing.rateCard.day.${day}`)}
                          </Chip>
                        ))}
                      </div>
                      {fieldErrors[FE.bdays(cls.id, index)] !== undefined ? <p className="clav-field__error">{fieldErrors[FE.bdays(cls.id, index)]}</p> : null}
                      <TextField
                        label={t('pricing.rateCard.bandStart', { class: cls.name })}
                        type="time"
                        value={band.start}
                        onChange={(e) => {
                          setBand(cls.id, index, { start: e.target.value });
                          clearField(FE.bend(cls.id, index));
                        }}
                        onBlur={() => setField(FE.bend(cls.id, index), bandTimeMsg(band.start, band.end))}
                      />
                      <TextField
                        label={t('pricing.rateCard.bandEnd', { class: cls.name })}
                        type="time"
                        value={band.end}
                        onChange={(e) => {
                          setBand(cls.id, index, { end: e.target.value });
                          clearField(FE.bend(cls.id, index));
                        }}
                        onBlur={() => setField(FE.bend(cls.id, index), bandTimeMsg(band.start, band.end))}
                        error={fieldErrors[FE.bend(cls.id, index)]}
                      />
                      <TextField
                        label={t('pricing.rateCard.bandRate', { class: cls.name })}
                        inputMode="decimal"
                        value={band.hourly}
                        onChange={(e) => {
                          setBand(cls.id, index, { hourly: e.target.value });
                          clearField(FE.brate(cls.id, index));
                        }}
                        onBlur={() => setField(FE.brate(cls.id, index), bandRateMsg(band.hourly))}
                        error={fieldErrors[FE.brate(cls.id, index)]}
                      />
                      <Button variant="ghost" onClick={() => setLine(cls.id, { bands: line.bands.filter((_, at) => at !== index) })}>
                        {t('pricing.rateCard.removeBand')}
                      </Button>
                    </div>
                  ))}
                  <Button variant="secondary" onClick={() => setLine(cls.id, { bands: [...line.bands, { days: ['mon', 'tue', 'wed', 'thu', 'fri'], start: '01:00', end: '11:00', hourly: line.hourly }] })}>
                    {t('pricing.rateCard.addBand')}
                  </Button>
                </fieldset>
              );
            })
          )
        ) : null}

        {step === 2 ? (
          <>
            <TextField
              label={t('pricing.rateCard.name')}
              required
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearField('name');
              }}
              onBlur={() => setField('name', name.trim() === '' ? t('pricing.rateCard.fieldError.name') : undefined)}
              error={fieldErrors.name}
            />
            <Switch checked={later} onCheckedChange={setLater} label={t('pricing.rateCard.startLater')} />
            {later ? <TextField label={t('pricing.rateCard.startsAt')} type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} /> : null}
            <ul className="console-ratecard__review">
              {classes.map((cls) => {
                const line = lines[cls.id]!;
                const hourly = majorToMinor(line.hourly, currency);
                return (
                  <li key={cls.id}>
                    <strong>{cls.name}</strong>{' '}
                    {line.included && hourly !== null
                      ? t('pricing.rateCard.reviewLine', {
                          rate: format.money({ amount: hourly, currency }),
                          step: t('pricing.rateCard.stepMinutes', { count: Number(line.unit) / 60 }),
                          bands: line.bands.length,
                          example: format.money(
                            exampleCharge({ amount: hourly, currency }, { billingUnitSeconds: Number(line.unit), minimumChargeSeconds: Number(line.minimumMinutes || '0') * 60 } as RateCardRate),
                          ),
                        })
                      : t('pricing.rateCard.flat', { rate: format.money(view.fallback.hourlyAmount) })}
                  </li>
                );
              })}
            </ul>
            <p className="console-subtle">{t('pricing.rateCard.reviewHint')}</p>
          </>
        ) : null}

        <div className="console-form__actions">
          {step > 0 ? (
            <Button variant="ghost" onClick={() => setStep(step - 1)}>
              {t('pricing.rateCard.back')}
            </Button>
          ) : null}
          <Button variant="primary" type="submit" busy={publish.isPending}>
            {step < 2 ? t('pricing.rateCard.next') : t('pricing.rateCard.publish')}
          </Button>
        </div>
      </form>
    </div>
  );
}
